import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api-response.model';
import { AuthTokenService } from './auth-token.service';
import { EntraIdLoginRequest, LoginRequest, LoginResponse } from './models/login.model';
import {
  ChangePasswordRequest,
  ValidateOtpRequest,
  ValidateOtpResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
} from './models/recovery-password.model';

const { username, password } = environment.recoveryPasswordAuth;
const RECOVERY_PASSWORD_AUTH = `Basic ${btoa(`${username}:${password}`)}`;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly authTokenService = inject(AuthTokenService);
  private readonly apiUrl = `${environment.apiBaseUrl}auth/`;
  private readonly recoveryPasswordHeaders = new HttpHeaders({ Authorization: RECOVERY_PASSWORD_AUTH });

  login(request: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<ApiResponse<LoginResponse>>(`${this.apiUrl}login`, request)
      .pipe(
        map((response) => response.data),
        tap((data) => this.startSession(data, request.usuario)),
      );
  }

  loginWithEntraId(request: EntraIdLoginRequest): Observable<LoginResponse> {
    return this.http
      .post<ApiResponse<LoginResponse>>(`${this.apiUrl}login-entra-id`, request)
      .pipe(
        map((response) => response.data),
        tap((data) => this.startSession(data, data.user.username ?? '')),
      );
  }

  verifyEmail(request: VerifyEmailRequest): Observable<VerifyEmailResponse> {
    return this.http
      .post<ApiResponse<VerifyEmailResponse>>(`${this.apiUrl}verificar-email`, request, {
        headers: this.recoveryPasswordHeaders,
      })
      .pipe(map((response) => response.data));
  }

  validateOtp(request: ValidateOtpRequest): Observable<ValidateOtpResponse> {
    return this.http
      .post<ApiResponse<ValidateOtpResponse>>(`${this.apiUrl}validar-codigo-otp`, request, {
        headers: this.recoveryPasswordHeaders,
      })
      .pipe(map((response) => response.data));
  }

  changePassword(request: ChangePasswordRequest): Observable<void> {
    return this.http
      .post<ApiResponse<unknown>>(`${this.apiUrl}cambiar-contrasenia`, request, {
        headers: this.recoveryPasswordHeaders,
      })
      .pipe(map(() => undefined));
  }

  private startSession(data: LoginResponse, username: string): void {
    this.authTokenService.setToken(data.accessToken);
    this.authTokenService.setUser(data.user);
    this.authTokenService.setExpiresAt(data.expiresAtUtc);
    this.authTokenService.setUsername(username);
    this.authTokenService.setCampaign(data.campaniaRuta ?? {})
  }
}
